/* Rig builder for The Racer's Mark. Reads tagged products from the section's JSON and builds the page. */
(function () {
  'use strict';

  const STEPS = [
    { id: 'chassis', name: 'Chassis', required: true, guide: 'gt-vs-formula-sim-racing-cockpit',
      help: 'Everything mounts to the chassis. GT gives an upright position; formula a reclined, legs-forward one.',
      filters: [['all', 'All'], ['gt', 'GT'], ['formula', 'Formula']] },
    { id: 'seat', name: 'Seat', required: false, guide: 'bucket-seat-vs-recliner-sim-racing',
      help: 'A bucket holds you in place under braking; a recliner is comfier for long sessions. Formula rigs need a formula seat.' },
    { id: 'wheelbase', name: 'Wheelbase', required: true, guide: 'how-much-wheelbase-torque-do-you-need',
      help: 'All direct drive. Most drivers are well served by 9 to 18 Nm.',
      filters: [['all', 'All'], ['lo', 'Under 12 Nm'], ['mid', '12–18 Nm'], ['hi', '25 Nm +']] },
    { id: 'wheel', name: 'Steering wheel', required: true, guide: 'gt-vs-formula-sim-racing-steering-wheel',
      help: 'GT for a mix of cars, formula for single-seaters, round for rally and drift.',
      filters: [['all', 'All'], ['gt', 'GT'], ['formula', 'Formula'], ['round', 'Round']] },
    { id: 'pedals', name: 'Pedals', required: true, guide: 'load-cell-vs-hydraulic-vs-active-sim-racing-pedals',
      help: 'A load cell brake measures pressure, which builds muscle memory.' },
    { id: 'monitor', name: 'Monitor stand', required: false, guide: 'rig-mounted-vs-freestanding-sim-racing-monitor-stand',
      help: 'Freestanding keeps wheelbase vibration off your screens; rig-mounted saves space.' },
    { id: 'extra', name: 'Extras', required: false, multi: true, guide: 'h-pattern-vs-sequential-shifter-handbrake',
      help: 'Shifters, a handbrake and a dash display. Pick as many as you like.' }
  ];
  const TIER = { 1: 'Entry', 2: 'Mid', 3: 'High' };
  const QR = { simagic: 'Simagic', moza: 'MOZA', fanatec: 'Fanatec', asetek: 'Asetek', simucube: 'Simucube' };
  const STORE_KEY = 'rb-build-v1';

  function init(root) {
    const sid = root.id.replace('rb-', '');
    const raw = JSON.parse(document.querySelector(`[data-rb-products="${sid}"]`).textContent || '[]');
    const cfg = JSON.parse(document.querySelector(`[data-rb-config="${sid}"]`).textContent || '{}');
    const base = (root.dataset.root || '/').replace(/\/?$/, '/');
    const $ = (k) => root.querySelector(`[data-rb="${k}"]`);
    const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: cfg.currency || 'USD' });
    const money = (cents) => fmt.format(cents / 100);
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    /* products → items */
    const items = {};
    const byStep = {};
    raw.forEach((p) => {
      const tags = (p.tags || []).filter((t) => t.indexOf('rig:') === 0).map((t) => t.slice(4));
      const val = (pre) => { const t = tags.find((x) => x.indexOf(pre) === 0); return t ? t.slice(pre.length) : null; };
      const step = val('step-');
      const vars = (p.vars || []).filter((v) => v.p > 0);
      if (!step || !vars.length) return;
      const it = {
        id: p.id, title: p.t, vendor: p.v, url: p.u, img: p.img, vars, step,
        layout: val('layout-'), tier: +val('tier-') || 0, maker: val('maker-'), nm: +val('torque-') || 0,
        qr: val('qr-'), wtype: val('wheel-'), zeus: tags.includes('zeus'), clutch: val('clutch-'),
        brakeOnly: tags.includes('brake-only'), mon: val('mon-'), part: val('part-'),
        hpattern: tags.includes('h-pattern'), zeusOnly: tags.includes('zeus-only'), seatType: val('seat-')
      };
      it.minPrice = Math.min.apply(null, vars.map((v) => v.p));
      it.meta = metaFor(it);
      items[it.id] = it;
      (byStep[step] = byStep[step] || []).push(it);
    });
    Object.values(byStep).forEach((list) => list.sort((a, b) => a.minPrice - b.minPrice));

    function metaFor(it) {
      switch (it.step) {
        case 'chassis': return `${it.layout === 'formula' ? 'Formula' : 'GT'}${it.tier ? ' · ' + TIER[it.tier] : ''}`;
        case 'seat': return it.layout === 'formula' ? 'Formula seat' : 'GT seat';
        case 'wheelbase': return it.nm ? `${it.nm} Nm direct drive` : 'Direct drive';
        case 'wheel': return { gt: 'GT', formula: 'Formula', round: 'Round' }[it.wtype] || '';
        case 'pedals': return it.brakeOnly ? 'Brake pedal only' : it.clutch === 'yes' ? 'Includes clutch' : it.clutch === 'no' ? 'Brake & throttle' : 'Pedal set';
        case 'monitor': return { rig: 'Rig-mounted', free: 'Freestanding', triple: 'Triple screens' }[it.mon] || '';
        case 'extra': return it.hpattern ? 'H-pattern shifter' : { shifter: 'Sequential shifter', handbrake: 'Handbrake', dash: 'Dash display' }[it.part] || '';
      }
      return '';
    }
    const firstVar = (it) => (it.vars.find((v) => v.a) || it.vars[0]).id;

    /* state: step → {pid, vid}; extra → [{pid, vid}] */
    let sel = { extra: [] }, skipped = {}, filt = {}, open = 'chassis';
    try {
      const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (saved && saved.sel) {
        sel = saved.sel; skipped = saved.skipped || {};
        STEPS.forEach((s) => {
          if (s.multi) sel.extra = (sel.extra || []).filter((x) => items[x.pid]);
          else if (sel[s.id] && !items[sel[s.id].pid]) sel[s.id] = null;
        });
        open = (STEPS.find((s) => !s.multi && !sel[s.id] && !skipped[s.id]) || STEPS[STEPS.length - 1]).id;
      }
    } catch (e) { /* storage unavailable */ }
    const save = () => { try { localStorage.setItem(STORE_KEY, JSON.stringify({ sel, skipped })); } catch (e) {} };

    const pick = (sid) => (sel[sid] ? items[sel[sid].pid] : null);
    const varOf = (entry) => items[entry.pid].vars.find((v) => v.id === entry.vid) || items[entry.pid].vars[0];
    const chosen = () => {
      const list = [];
      STEPS.forEach((s) => {
        if (s.multi) (sel.extra || []).forEach((e) => list.push({ step: s, it: items[e.pid], v: varOf(e) }));
        else if (sel[s.id]) list.push({ step: s, it: items[sel[s.id].pid], v: varOf(sel[s.id]) });
      });
      return list;
    };
    const guide = (h, text) => (cfg.guides ? ` <a href="${base}blogs/news/${h}">${text}</a>` : '');

    function warnings() {
      const W = [];
      const c = pick('chassis'), s = pick('seat'), b = pick('wheelbase'), w = pick('wheel'), p = pick('pedals');
      const ex = (sel.extra || []).map((e) => items[e.pid]);
      const add = (level, step, text, id) => W.push({ level, step, text, id });
      if (b && c && c.tier === 1 && b.nm >= 18)
        add('warn', 'wheelbase', `The ${b.title} (${b.nm} Nm) is a lot of torque for an entry cockpit. For a base this strong we recommend a mid or high tier chassis.`, b.id);
      if (b && !c && skipped.chassis && b.nm >= 12)
        add('info', 'wheelbase', `Make sure your current rig is rigid enough for ${b.nm} Nm. Desks and wheel stands flex and soak up detail.`, b.id);
      if (w && b) {
        if ((w.qr === 'simagic' || w.qr === 'moza') && w.qr !== b.qr)
          add('warn', 'wheel', `The ${w.title} is made for ${QR[w.qr]} bases. On the ${b.title} it needs a compatible quick release or adapter. Contact us to confirm before ordering.`, w.id);
        else if (w.qr === 'universal')
          add('info', 'wheel', `The ${w.title} needs the right quick release for the ${b.title}. We'll confirm which one when you order.`, w.id);
      }
      if (c && w) {
        if (c.layout === 'formula' && w.wtype !== 'formula') add('info', 'wheel', 'A formula wheel suits the close, reclined position of a formula chassis better than a full rim.', w.id);
        if (c.layout === 'gt' && w.wtype === 'formula') add('info', 'wheel', 'Formula wheels have less steering lock. Great for single-seaters, less so for road cars.', w.id);
      }
      if (c && s) {
        if (c.layout === 'formula' && s.layout !== 'formula') add('warn', 'seat', 'This seat is made for GT rigs. On a formula chassis, choose a formula seat.', s.id);
        if (c.layout === 'gt' && s.layout === 'formula') add('warn', 'seat', 'This seat is made for formula chassis. Pick a bucket or recliner for a GT rig.', s.id);
        if (c.layout === 'gt' && s.maker && c.maker && s.maker !== c.maker) add('info', 'seat', 'Mixing brands is fine, but you may need a seat bracket set to mount it. Ask us and we will add the right one.', s.id);
      }
      const mn = pick('monitor');
      if (mn && c && mn.mon === 'rig' && c.layout === 'formula')
        add('warn', 'monitor', `The ${mn.title} bolts to GT chassis only and won't fit a formula chassis. Choose a freestanding monitor stand instead.`, mn.id);
      if (p && p.brakeOnly) add('warn', 'pedals', 'This is the brake pedal only. Add a throttle, or choose a full set.', p.id);
      const h = ex.find((e) => e.hpattern);
      if (h && p && p.clutch === 'no')
        add('warn', 'extra', `An H-pattern shifter needs a clutch pedal, and the ${p.title} doesn't include one. <a href="${base}collections/pedals">Add a clutch pedal</a> or pick a 3-pedal set.`, h.id);
      const md = ex.find((e) => e.zeusOnly);
      if (md && !(w && w.zeus)) add('warn', 'extra', `The ${md.title} mounts to Simagic Zeus wheels. Pick a Zeus wheel or a standalone dash.`, md.id);
      if (c && ex.some((e) => e.part === 'shifter' || e.part === 'handbrake'))
        add('info', 'extra', `You'll need a shifter and handbrake plate for your chassis. <a href="${base}collections/shifters">See mounting plates</a>.`, null);
      if (ex.filter((e) => e.part === 'dash').length > 1) add('info', 'extra', 'You have picked two dash displays. Most rigs only need one.', null);
      return W;
    }

    /* rendering */
    const stepsEl = $('steps');
    const thumb = (it) => it.img
      ? `<img src="${esc(it.img)}" alt="" loading="lazy" width="480" height="360">`
      : `<span class="rb-mono">${esc((it.vendor || '?').slice(0, 2).toUpperCase())}</span>`;
    function filtered(s) {
      let list = byStep[s.id] || [];
      const f = filt[s.id] || 'all';
      if (f === 'all') {
        if (s.id === 'seat' && pick('chassis')) { const L = pick('chassis').layout; list = list.slice().sort((a, b) => (b.layout === L) - (a.layout === L)); }
        return list;
      }
      if (s.id === 'chassis') return list.filter((x) => x.layout === f);
      if (s.id === 'wheel') return list.filter((x) => x.wtype === f);
      if (s.id === 'wheelbase') return list.filter((x) => (f === 'lo' ? x.nm < 12 : f === 'mid' ? x.nm >= 12 && x.nm <= 18 : x.nm >= 25));
      return list;
    }
    function isOn(s, id) { return s.multi ? (sel.extra || []).some((e) => e.pid === id) : !!(sel[s.id] && sel[s.id].pid === id); }
    function entryOf(s, id) { return s.multi ? (sel.extra || []).find((e) => e.pid === id) : sel[s.id] && sel[s.id].pid === id ? sel[s.id] : null; }

    function renderSteps() {
      const W = warnings();
      stepsEl.innerHTML = STEPS.map((s, i) => {
        const stepW = W.filter((x) => x.step === s.id);
        const flagged = stepW.some((x) => x.level === 'warn');
        const names = s.multi ? (sel.extra || []).map((e) => items[e.pid].title).join(', ') : pick(s.id) ? pick(s.id).title : '';
        const done = s.multi ? (sel.extra || []).length > 0 : !!sel[s.id] || !!skipped[s.id];
        const list = filtered(s);
        const f = filt[s.id] || 'all';
        const cards = list.map((it) => {
          const on = isOn(s, it.id), en = entryOf(s, it.id);
          const v = en ? varOf(en) : it.vars.find((x) => x.a) || it.vars[0];
          const sold = !it.vars.some((x) => x.a);
          const varSel = it.vars.length > 1
            ? `<select aria-label="Option for ${esc(it.title)}" data-var="${s.id}:${it.id}">${it.vars.map((x) => `<option value="${x.id}" ${x.id === v.id ? 'selected' : ''} ${x.a ? '' : 'disabled'}>${esc(x.t)} · ${money(x.p)}${x.a ? '' : ' (sold out)'}</option>`).join('')}</select>` : '';
          return `<div class="rb-card ${on ? 'on' : ''} ${sold ? 'sold' : ''} ${stepW.some((x) => x.id === it.id && x.level === 'warn') ? 'flag' : ''}">
            <button type="button" class="rb-pick" data-pick="${s.id}:${it.id}" aria-pressed="${on}" ${sold ? 'disabled' : ''}>
              <span class="rb-thumb">${thumb(it)}</span>
              <span class="rb-name">${esc(it.title)}</span>
              <span class="rb-row"><span class="rb-meta">${sold ? 'Sold out' : esc(it.meta)}</span><span class="rb-price">${it.vars.length > 1 && !on ? 'from ' : ''}${money(on ? v.p : it.minPrice)}</span></span>
            </button>
            <span class="rb-flag" aria-hidden="true"></span><span class="rb-tick" aria-hidden="true">✓</span>
            <div class="rb-card-foot">${varSel}<a href="${esc(it.url)}" target="_blank" rel="noopener">View details</a></div>
          </div>`;
        }).join('');
        return `<section class="rb-step ${open === s.id ? 'open' : ''} ${done ? 'done' : ''} ${flagged ? 'flagged' : ''}" id="rb-step-${s.id}">
          <button class="rb-step-head" type="button" data-toggle="${s.id}" aria-expanded="${open === s.id}">
            <span class="rb-num">${flagged ? '!' : done ? '✓' : i + 1}</span>
            <span class="rb-step-title"><span class="rb-stitle">${s.name}<span class="rb-req">${s.required ? 'Required' : 'Optional'}</span></span>
            <span class="rb-chosen">${esc(names || (skipped[s.id] ? 'Using one I already own' : 'Not chosen yet'))}</span></span>
            <svg class="rb-chev" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>
          </button>
          <div class="rb-step-body">
            <p class="rb-help">${s.help}${guide(s.guide, 'Read our guide')}</p>
            ${s.filters ? `<div class="rb-chips" role="group" aria-label="Filter ${s.name}">${s.filters.map(([k, l]) => `<button type="button" class="rb-chip" data-filter="${s.id}:${k}" aria-pressed="${f === k}">${l}</button>`).join('')}</div>` : ''}
            ${list.length ? `<div class="rb-grid">${cards}</div>` : '<p class="rb-empty">Nothing matches this filter right now.</p>'}
            ${stepW.length ? `<div class="rb-alerts">${stepW.map((a) => `<div class="rb-alert ${a.level}"><b>${a.level === 'warn' ? 'Check' : 'Tip'}</b><span>${a.text}</span></div>`).join('')}</div>` : ''}
            <div class="rb-step-foot">
              ${s.multi ? '<span></span>' : `<button type="button" class="rb-skip" data-skip="${s.id}" aria-pressed="${!!skipped[s.id]}">${skipped[s.id] ? 'Using one I already own' : 'I already own one, skip this step'}</button>`}
              ${i < STEPS.length - 1 ? `<button type="button" class="rb-btn rb-btn--quiet" data-next="${STEPS[i + 1].id}">Next: ${STEPS[i + 1].name}</button>` : ''}
            </div>
          </div>
        </section>`;
      }).join('');
    }

    const rig = $('rig');
    rig.querySelectorAll('.rb-part').forEach((g) => {
      const src = g.querySelector('.rb-src');
      const ghost = src.cloneNode(true);
      ghost.setAttribute('class', 'rb-ghost');
      src.setAttribute('class', 'rb-solid');
      src.querySelectorAll('rect,path,circle,ellipse,line,polyline').forEach((el) => el.setAttribute('pathLength', '1'));
      g.insertBefore(ghost, src);
    });
    function partOn(name, on) {
      // a part can have more than one layer (e.g. chassis frame behind and in front of the seat)
      rig.querySelectorAll(`[data-part="${name}"]`).forEach((g) => {
        const was = g.classList.contains('on');
        if (on && !was) { g.classList.add('on', 'pop'); setTimeout(() => g.classList.remove('pop'), 900); }
        if (!on && was) g.classList.remove('on');
      });
    }
    function renderRig() {
      const c = pick('chassis'), w = pick('wheel'), m = pick('monitor');
      const formula = !!(c && c.layout === 'formula');
      rig.classList.toggle('formula', formula);
      $('layout').textContent = formula ? 'Formula layout' : 'GT layout';
      rig.dataset.wheel = w ? (w.wtype === 'formula' ? 'f' : w.wtype === 'round' ? 'round' : 'gt') : formula ? 'f' : 'gt';
      rig.dataset.mon = m && m.mon ? m.mon : 'free';
      const st = pick('seat');
      rig.dataset.seat = st && st.seatType === 'recliner' ? 'recliner' : 'bucket';
      partOn('chassis', !!c); partOn('seat', !!sel.seat); partOn('wheelbase', !!sel.wheelbase);
      partOn('wheel', !!w); partOn('pedals', !!sel.pedals); partOn('monitor', !!m);
      const ex = (sel.extra || []).map((e) => items[e.pid]);
      ['shifter', 'handbrake', 'dash'].forEach((p) => partOn(p, ex.some((e) => e.part === p)));
    }
    function renderSummary() {
      const rows = STEPS.filter((s) => !s.multi).map((s) => {
        const it = pick(s.id);
        return `<li><span class="k">${s.name}</span><span class="v ${it ? '' : 'none'}">${it ? esc(it.title) : skipped[s.id] ? 'Already own' : '—'}</span><span class="p">${it ? money(varOf(sel[s.id]).p) : ''}</span></li>`;
      });
      (sel.extra || []).forEach((e) => rows.push(`<li><span class="k">Extra</span><span class="v">${esc(items[e.pid].title)}</span><span class="p">${money(varOf(e).p)}</span></li>`));
      $('lines').innerHTML = rows.join('');
      const parts = chosen();
      const sum = parts.reduce((a, x) => a + x.v.p, 0);
      $('total').innerHTML = `${money(sum)}<small>${parts.length ? `${parts.length} part${parts.length > 1 ? 's' : ''}${cfg.taxNote ? ' · ' + esc(cfg.taxNote) : ''}` : 'No parts chosen yet'}</small>`;
      const W = warnings().filter((x) => x.level === 'warn');
      $('alerts').innerHTML = W.length ? `<div class="rb-alert warn"><b>Check</b><span>${W.length} item${W.length > 1 ? 's need' : ' needs'} a look before you order. Open the flagged step${W.length > 1 ? 's' : ''} for details.</span></div>` : '';
      const missing = STEPS.filter((s) => s.required && !sel[s.id] && !skipped[s.id]);
      const done = STEPS.filter((s) => (s.multi ? (sel.extra || []).length : sel[s.id] || skipped[s.id])).length;
      $('progress').textContent = `${done} of ${STEPS.length} steps`;
      $('add').disabled = parts.length === 0;
      const hint = $('hint');
      hint.classList.remove('err');
      hint.textContent = parts.length && missing.length ? `Still to choose: ${missing.map((s) => s.name.toLowerCase()).join(', ')}.` : '';
    }
    function render() { renderSteps(); renderRig(); renderSummary(); save(); }

    stepsEl.addEventListener('click', (e) => {
      const t = e.target.closest('button');
      if (!t || !stepsEl.contains(t)) return;
      if (t.dataset.toggle) { open = open === t.dataset.toggle ? null : t.dataset.toggle; renderSteps(); return; }
      if (t.dataset.filter) { const [s, k] = t.dataset.filter.split(':'); filt[s] = k; renderSteps(); return; }
      if (t.dataset.skip) { const s = t.dataset.skip; skipped[s] = !skipped[s]; if (skipped[s]) sel[s] = null; render(); return; }
      if (t.dataset.next) {
        open = t.dataset.next; renderSteps();
        const el = root.querySelector('#rb-step-' + open);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (t.dataset.pick) {
        const [s, idStr] = t.dataset.pick.split(':'); const id = +idStr; const it = items[id];
        if (s === 'extra') {
          sel.extra = (sel.extra || []).some((x) => x.pid === id) ? sel.extra.filter((x) => x.pid !== id) : [...(sel.extra || []), { pid: id, vid: firstVar(it) }];
        } else {
          sel[s] = sel[s] && sel[s].pid === id ? null : { pid: id, vid: firstVar(it) };
          skipped[s] = false;
        }
        render();
      }
    });
    stepsEl.addEventListener('change', (e) => {
      const t = e.target;
      if (!t.dataset || !t.dataset.var) return;
      const [s, idStr] = t.dataset.var.split(':'); const id = +idStr; const vid = +t.value;
      if (s === 'extra') {
        const ex = (sel.extra || []).find((x) => x.pid === id);
        if (ex) ex.vid = vid; else sel.extra = [...(sel.extra || []), { pid: id, vid }];
      } else { sel[s] = { pid: id, vid }; skipped[s] = false; }
      render();
    });
    $('reset').addEventListener('click', () => { sel = { extra: [] }; skipped = {}; filt = {}; open = 'chassis'; render(); });

    $('add').addEventListener('click', async () => {
      const btn = $('add'), hint = $('hint');
      const parts = chosen();
      if (!parts.length) return;
      btn.disabled = true; btn.textContent = 'Adding…';
      const notes = warnings().filter((x) => x.level === 'warn').map((x) => x.text.replace(/<[^>]+>/g, ''));
      try {
        if (notes.length) {
          await fetch(base + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { 'Rig builder notes': notes.join(' | ') } }) });
        }
        const res = await fetch(base + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: parts.map((x) => ({ id: x.v.id, quantity: 1, properties: { _rig_builder: 'yes' } })) }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong.');
        window.location.href = base + 'cart';
      } catch (err) {
        hint.classList.add('err');
        hint.textContent = `We couldn't add your rig to the cart: ${err.message} Try again, or contact us and we'll put the order together for you.`;
        btn.disabled = false; btn.textContent = 'Add rig to cart';
      }
    });

    render();
  }

  function boot() { document.querySelectorAll('.rb[id^="rb-"]').forEach((el) => { if (!el.dataset.rbReady) { el.dataset.rbReady = '1'; init(el); } }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
